import { generateOperationKey } from "@orpc/tanstack-query";
import { Logo } from "@/components/logo";
import { useSignOut } from "@/hooks/use-sign-out";
import { authViewPaths, nonAuthViews, type AuthViewPath } from "@/types";
import { AuthView, OrganizationSwitcher, UserInvitationsCard } from "@papier/auth-ui";
import { Button } from "@papier/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@papier/ui/components/ui/card";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { z } from "zod";

export const Route = createFileRoute("/auth/$authView")({
  component: AuthPages,
  params: {
    parse: ({ authView }) => ({
      authView: z
        .enum(Object.values(authViewPaths) as [AuthViewPath, ...AuthViewPath[]])
        .parse(authView),
    }),
  },
  validateSearch: z.object({
    redirectTo: z.string().optional(),
    plan: z.literal("pro").optional(),
  }),
  beforeLoad: async ({
    context: { queryClient, authOptions },
    search: { redirectTo, plan },
    params: { authView },
  }) => {
    const [session, hasOrgs] = await Promise.all([
      queryClient.fetchQuery(authOptions.session),
      queryClient.fetchQuery(authOptions.hasOrganizations),
    ]);

    if (authView === "organization") {
      if (!session) {
        throw redirect({
          to: "/auth/$authView",
          params: { authView: "sign-in" },
          search: { redirectTo: "/auth/organization", plan },
        });
      }

      if (hasOrgs && session.session.activeOrganizationId) {
        throw redirect({
          to: redirectTo ?? "/",
          search: { plan },
        });
      }
    } else {
      if (session && nonAuthViews.includes(authView)) {
        throw redirect({
          to: redirectTo ?? "/",
          search: { plan },
        });
      }
    }
  },
});

const links = [
  {
    title: "Docs",
    href: "/docs",
  },
  {
    title: "Pricing",
    href: "/pricing",
  },
  {
    title: "Help",
    href: "/contact",
  },
];

function AuthPages() {
  const { useParams, useRouteContext, useSearch } = Route;
  const { authView } = useParams();
  const { redirectTo, plan } = useSearch();
  const { queryClient } = useRouteContext();
  const router = useRouter();
  const handleSignOut = useSignOut();

  return (
    <main className="relative flex min-h-screen flex-col pt-10">
      <div className="flex flex-1 flex-col items-center justify-center px-4">
        <a
          href="https://usepapier.io"
          aria-label="home"
          className="pb-10"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Logo className="text-4xl" />
        </a>
        {authView === "organization" ? (
          <Card className="w-full max-w-3xl">
            <CardHeader>
              <CardTitle className="text-lg font-bold">Continue with an Organization</CardTitle>
              <CardDescription>
                If you are not a member yet, you can accept an invitation or create a new
                organization below.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
              <OrganizationSwitcher
                onSetActive={async () => {
                  await queryClient.resetQueries();
                  await router.invalidate();
                  void router.navigate({ to: redirectTo ?? "/", search: { plan } });
                }}
              />
              <UserInvitationsCard
                onAcceptInvitation={async () => {
                  await queryClient.resetQueries();
                  await router.invalidate();
                  void router.navigate({ to: redirectTo ?? "/", search: { plan } });
                }}
                onInvalidateInvitations={async () => {
                  void queryClient.invalidateQueries({
                    queryKey: generateOperationKey(["authTrpc", "getUserInvitations"]),
                  });
                  void queryClient.invalidateQueries({
                    queryKey: generateOperationKey(["authTrpc", "getUserOrganizations"]),
                  });
                }}
              />
            </CardContent>
            <CardFooter className="flex items-center justify-center">
              <p className="text-sm text-muted-foreground">Want to switch accounts?</p>
              <Button variant={"link"} onClick={handleSignOut}>
                Sign out
              </Button>
            </CardFooter>
          </Card>
        ) : (
          <AuthView pathname={authView} redirectTo={redirectTo} />
        )}
      </div>

      <footer className="mt-10 p-6">
        <div className="mx-auto max-w-4xl">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <a
              href="https://usepapier.io"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="home"
            >
              <Logo />
            </a>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              {links.map((link) => (
                <a
                  key={link.title}
                  href={`https://usepapier.io${link.href}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-muted-foreground duration-150 hover:text-primary"
                >
                  <span>{link.title}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}
